import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useToast } from '../context/ToastContext';
import { useOrganization } from '../context/OrganizationContext';
import { useAuth } from '../context/AuthContext';

export default function CreateFirstOrgModal({ onComplete }) {
  const { user } = useAuth();
  const { addToast } = useToast();
  const { refreshOrganizations, currentOrganization } = useOrganization();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1 = org details, 2 = first project
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    projectName: '',
    projectDescription: ''
  });
  const [slugAvailable, setSlugAvailable] = useState(true);
  const [checkingSlug, setCheckingSlug] = useState(false);

  // Handle org name to slug conversion
  useEffect(() => {
    if (formData.name) {
      const generatedSlug = formData.name.toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .trim();
      
      setFormData(prev => ({
        ...prev,
        slug: generatedSlug
      }));
    }
  }, [formData.name]);

  // Check slug availability with debounce
  useEffect(() => {
    const checkSlug = async () => {
      if (!formData.slug) return;
      setCheckingSlug(true);
      try {
        const { data, error } = await supabase
          .from('organizations')
          .select('id')
          .eq('slug', formData.slug)
          .single();

        setSlugAvailable(!data);
        if (error && error.code !== 'PGRST116') throw error;
      } catch (error) {
        console.error('Error checking slug:', error);
      } finally {
        setCheckingSlug(false);
      }
    };

    const timeoutId = setTimeout(checkSlug, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.slug]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateOrganization = async () => {
    if (!slugAvailable) {
      addToast('Organization URL is not available', 'error');
      return;
    }

    setLoading(true);
    try {
      // Create organization
      const { data: org, error: orgError } = await supabase
        .from('organizations')
        .insert({
          name: formData.name,
          slug: formData.slug,
          description: formData.description,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (orgError) throw orgError;

      // Add user as owner
      const { error: memberError } = await supabase
        .from('organization_members')
        .insert({
          organization_id: org.id,
          user_id: user.id,
          role: 'owner',
          created_at: new Date().toISOString()
        });

      if (memberError) throw memberError;

      await refreshOrganizations();
      setStep(2); // Move to project creation
    } catch (error) {
      console.error('Error creating organization:', error);
      addToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    if (!currentOrganization?.id) {
      addToast('Organization not found', 'error');
      return;
    }

    setLoading(true);
    try {
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .insert({
          name: formData.projectName,
          description: formData.projectDescription,
          organization_id: currentOrganization.id,
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (projectError) throw projectError;

      addToast('Project created successfully', 'success');
      onComplete?.();
    } catch (error) {
      console.error('Error creating project:', error);
      addToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-dark-200 rounded-lg shadow-xl max-w-2xl w-full mx-4 p-8">
        {step === 1 ? (
          <>
            <h2 className="text-2xl font-bold text-white mb-2 text-center">Welcome to CliveAI</h2>
            <p className="text-gray-400 mb-8 text-center">
              Let's set up your organization
            </p>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Organization Name*
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-dark-300 border border-dark-400 rounded focus:outline-none focus:ring-2 focus:ring-accent-primary text-white"
                  placeholder="Enter organization name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Organization URL*
                </label>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <input
                      type="text"
                      name="slug"
                      value={formData.slug}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 bg-dark-300 border border-dark-400 rounded focus:outline-none focus:ring-2 focus:ring-accent-primary text-white ${
                        !slugAvailable ? 'border-red-500' : ''
                      }`}
                      placeholder="your-organization"
                      required
                    />
                  </div>

                  <div className="bg-dark-300 rounded p-4 space-y-2">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-400">Your organization URL will be:</span>
                      <span className="text-sm font-medium text-white">
                        https://{formData.slug || 'your-organization'}.cliveai.com
                      </span>
                    </div>

                    <div className="text-sm">
                      {checkingSlug ? (
                        <p className="text-gray-400">Checking availability...</p>
                      ) : !slugAvailable ? (
                        <p className="text-red-500">This URL is not available</p>
                      ) : formData.slug && (
                        <p className="text-green-500">✓ URL is available</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 bg-dark-300 border border-dark-400 rounded focus:outline-none focus:ring-2 focus:ring-accent-primary text-white"
                  placeholder="Brief description of your organization"
                />
              </div>

              <button
                onClick={handleCreateOrganization}
                disabled={loading || !formData.name || !formData.slug || !slugAvailable}
                className="w-full px-4 py-2 bg-accent-primary text-white rounded hover:bg-accent-primary-dark disabled:opacity-50 transition-colors"
              >
                {loading ? 'Creating Organization...' : 'Continue'}
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-2xl font-bold text-white mb-2 text-center">Create Your First Project</h2>
            <p className="text-gray-400 mb-8 text-center">
              Projects help you organize your content and workflows
            </p>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Project Name*
                </label>
                <input
                  type="text"
                  name="projectName"
                  value={formData.projectName}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-dark-300 border border-dark-400 rounded focus:outline-none focus:ring-2 focus:ring-accent-primary text-white"
                  placeholder="Enter project name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Project Description
                </label>
                <textarea
                  name="projectDescription"
                  value={formData.projectDescription}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 bg-dark-300 border border-dark-400 rounded focus:outline-none focus:ring-2 focus:ring-accent-primary text-white"
                  placeholder="Brief description of your project"
                />
              </div>

              <button
                onClick={handleCreateProject}
                disabled={loading || !formData.projectName}
                className="w-full px-4 py-2 bg-accent-primary text-white rounded hover:bg-accent-primary-dark disabled:opacity-50 transition-colors"
              >
                {loading ? 'Creating Project...' : 'Create Project & Get Started'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
} 